import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isFirefox, isIOS, isSafari } from 'react-device-detect';
import tinycolor from 'tinycolor2';
import { Slider, withStyles } from '@material-ui/core';

import { SCREEN_TYPE, heartAnimation, joinClassNames, toHHMMSS, enterFullscreen, exitFullscreen, isUsedOnMobile } from '../../../util/util';
import { setShowChatWindow, setShowShoppingCart } from '../../../actions/playerActions';
import { setOpenSettingMenu, setShowTranscriptionsModule } from '../../../actions/configurationActions';
import { LiveHelper } from '../../../util/live-helper';
import { NumberHelper } from '../../../util/number-helper';

import * as QPlayerSVGElement from '../../QPlayerSVGElement';
import ControlButton from '../ControlButton';

interface BottomControlSettingProps {
	settings: any;
	playerHover?: boolean;
	isSmallScreen?: boolean;
	isInlinePlayer?: boolean;
	isMaintainProportions?: boolean;
	queryParams?: any;
	isShowTimeline?: boolean;
	onToggleAdditionalMenu?: (_showAdditionalMenu?: boolean) => void;
	alignVideoOnAndroidDevices?: (_timeOut: number, _isReset: boolean) => void;
}

const VolumeSlider = withStyles({
	root: {
		color: 'inherit',
		height: 4,
	},
	thumb: {
		height: 16,
		width: 16,
		backgroundColor: '#ffffff',
		border: '2px solid #d4d3cf',
		marginTop: -6,
		marginLeft: -12,
		'&:focus, &:hover, &active, &.MuiSlider-active': {
			boxShadow: 'inherit',
		},
	},
	track: {
		backgroundColor: '#fff',
		height: 4,
		borderRadius: 4,
	},
	rail: {
		backgroundColor: '#fff',
		height: 4,
		borderRadius: 4,
	},
})(Slider);

const BottomControlSetting: React.FC<BottomControlSettingProps> = (props: BottomControlSettingProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const {
		currentVolumeLevel,
		currentPosition,
		duration: durationReducer,
		isPlaying,
		showShoppingCart,
		showChatWindow,
		reactionId,
	} = useSelector((state) => (state as any).playerReducer);
	const {
		isMediaLive,
		reactionUrl,
		mediaName,
		currentMediaId,
		accountId,
		mediasData,
		screenType,
		openSettingMenu,
		viewerEngagementModuleActivated,
		playlistId,
		embedShareSettings,
		mediaType,
		transcriptions,
	} = useSelector((state) => (state as any).configurationReducer);
	const { userId, username, email } = useSelector((state) => (state as any).chatLoginReducer);
	const { cartQuantity } = useSelector((state) => (state as any).shoppingReducer);

	const [fullscreenMode, setFullscreenMode] = useState(false);
	const [volumeHovered, setVolumeHovered] = useState(false);

	const bottomControlbarRight = useRef<HTMLElement | null>(null);

	const currentMedia = useMemo(
		() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
		[mediasData, currentMediaId]
	);

	const tabIndexValue = 0; // smaller device or screen size should also tab-able.
	const currentPlayerId = `player-${playlistId || currentMediaId}`;
	const hasAudio = !!(currentMedia?.audioTracks ?? []).length && props.settings.bottomControls.showAudioTracksSettings;
	const hasSubtitle = !!(currentMedia?.subtitles ?? []).length && props.settings.bottomControls.showSubtitlesSettings;
	const showTranscriptSettings = props.settings.bottomControls.showTranscriptSettings && transcriptions?.entries?.length > 0;
	let volumeBeforeMute = 0.5;

	const color = props.settings?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(props.settings?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const primaryColor = { backgroundColor: color };
	const hoverStyle = isUsedOnMobile(props.queryParams) ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	const duration = currentMedia?.duration ?? durationReducer;
	const isAtLive = useMemo(() => LiveHelper.isAtLive(isMediaLive, currentPosition, duration), [currentPosition, duration, isMediaLive]);
	const times = (
		props.isSmallScreen || screenType === SCREEN_TYPE.small ? toHHMMSS(currentPosition) : toHHMMSS(currentPosition) + '/' + toHHMMSS(duration)
	).toString();

	const showCenterControls = useMemo(() => {
		if (screenType === SCREEN_TYPE.mobile && !isUsedOnMobile(props.queryParams)) {
			return true;
		} else {
			return (
				props.isInlinePlayer &&
				props.settings?.bottomControls?.playPausePosition === 'center' &&
				(props.isSmallScreen || screenType === SCREEN_TYPE.mobile || screenType === SCREEN_TYPE.small)
			);
		}
	}, [props.isInlinePlayer, props.isSmallScreen, props.settings?.bottomControls?.playPausePosition, screenType, props.queryParams]);

	const getVolumePercentage = (value: number) => {
		let result = Math.round(value * 100);
		return result;
	};

	const handleButtonMute = () => {
		volumeBeforeMute = currentVolumeLevel;
		(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(0);
	};

	const handleButtonUnmute = () => {
		(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(volumeBeforeMute);
	};

	const goToLive = () => {
		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
	};

	const handleButtonSettings = (e: React.MouseEvent<HTMLElement>) => {
		if (openSettingMenu?.type === 'quality') {
			dispatch(setOpenSettingMenu(undefined));
			return;
		}

		dispatch(
			setOpenSettingMenu({
				type: 'quality',
				anchor: e.target ?? bottomControlbarRight?.current,
				isKeyboardTriggered: e.detail === 0
			})
		);
	};

	const handleButtonLanguageMenu = (e: React.MouseEvent<HTMLElement>) => {
		if (openSettingMenu?.type === 'language') {
			dispatch(setOpenSettingMenu(undefined));
			return;
		}

		dispatch(
			setOpenSettingMenu({
				type: 'language',
				anchor: e.target ?? bottomControlbarRight?.current,
				isKeyboardTriggered: e.detail === 0,
			})
		);
	};

	const handleButtonFullscreenEnter = () => {
		enterFullscreen(currentPlayerId);
		setFullscreenMode(true);
		props.alignVideoOnAndroidDevices?.(300, false);
	};

	const handleButtonFullscreenExit = () => {
		exitFullscreen(currentPlayerId);
		setFullscreenMode(false);
		props.alignVideoOnAndroidDevices?.(0, true);
	};

	const handleFullscreenchange = useCallback(() => {
		const isFullScreen =
			!!document.fullscreenElement ||
			(document as any).fullscreen ||
			(document as any).isFullScreen ||
			(document as any).msIsFullScreen ||
			(document as any).mozIsFullScreen ||
			(document as any).webkitIsFullScreen;
		setFullscreenMode(isFullScreen);

		const playerElem = document.getElementById(currentPlayerId);
		const transitionElem = document.querySelector('.transition-wrapper') as any;
		const interactContainer = document.querySelector('.interactOverlayContainer') as any;
		if (!playerElem) {
			return;
		}

		const screenHeight = embedShareSettings?.screenHeight ?? window.screen.height;
		const screenWidth = embedShareSettings?.screenWidth ?? window.screen.width;
		const ratio = 100 * (screenHeight / screenWidth);
		if (isFullScreen) {
			if (props.isMaintainProportions === false) {
				setTimeout(() => {
					playerElem.style.width = `${ratio}` + 'vh';
					if (transitionElem) {
						transitionElem.style.width = `${ratio}` + 'vh';
					}
					if (interactContainer) {
						interactContainer.style.width = `${ratio}` + 'vh';
					}
				}, 0);
			}
		} else {
			setTimeout(() => {
				if (props.isInlinePlayer) {
					playerElem.style.width = '100%';
					if (transitionElem) {
						transitionElem.style.width = '100%';
					}
					if (interactContainer) {
						interactContainer.style.width = '100%';
					}
				} else if (props.isMaintainProportions === false) {
					setTimeout(() => {
						playerElem.style.width = `${ratio}` + 'vh';
						if (transitionElem) {
							transitionElem.style.width = `${ratio}` + 'vh';
						}
						if (interactContainer) {
							interactContainer.style.width = `${ratio}` + 'vh';
						}
					}, 0);
				}
			}, 0);
		}
	}, [currentPlayerId, embedShareSettings, props.isInlinePlayer, props.isMaintainProportions]);

	const handleChromecastBtnClick = useCallback(() => {
		(window as any).GoBrain.widgets(currentPlayerId)?.openChromecastMenu?.();
	}, [currentPlayerId]);

	const handlePictureInPictureBtnClick = useCallback(() => {
		if (document.pictureInPictureElement) {
			(window as any).GoBrain.widgets(currentPlayerId)?.exitPictureInPicture?.();
		} else {
			(window as any).GoBrain.widgets(currentPlayerId)?.pictureInPicture?.();
		}
	}, [currentPlayerId]);

	const handleAirPlayBtnClick = useCallback(() => {
		(window as any).GoBrain.widgets(currentPlayerId)?.airplay?.();
	}, [currentPlayerId]);

	const handleButtonPlayPauseClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		if (isPlaying) {
			(window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
		} else {
			(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
		}
	};

	const handleKeyDownOnButton = (e: React.KeyboardEvent, callback?: () => void) => {
		if (e.key === 'Enter' || e.key === 'Space' || e.keyCode === 32 || e.keyCode === 13) {
			e.stopPropagation();
			callback?.();
		}
	};

	const handleButtonRewind = () => {
		let rewindPosition = currentPosition - 15;
		if (rewindPosition < 0) {
			rewindPosition = 0;
		}
		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(rewindPosition);
	};

	const handleButtonForward = () => {
		let forwardPosition = currentPosition + 15;
		if (isMediaLive && forwardPosition >= duration) {
			goToLive();
			return;
		}

		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(forwardPosition);
	};

	const handleKeydownVolumeButton = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === 'Space' || e.keyCode === 32 || e.keyCode === 13) {
			e.preventDefault();
			e.stopPropagation();
			if (currentVolumeLevel !== 0) {
				handleButtonMute();
			} else {
				handleButtonUnmute();
			}
		}

		if (e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.keyCode === 40 || e.keyCode === 37) {
			e.preventDefault();
			e.stopPropagation();
			handleButtonReduceVolume();
		}
		if (e.key === 'ArrowUp' || e.key === 'ArrowRight' || e.keyCode === 38 || e.keyCode === 39) {
			e.preventDefault();
			e.stopPropagation();
			handleButtonIncreaseVolume();
		}
	};

	const handleButtonReduceVolume = () => {
		let resultVolume = currentVolumeLevel - 0.05;
		if (resultVolume >= 0) {
			(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(resultVolume);
		} else {
			(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(0);
		}
	};

	const handleButtonIncreaseVolume = () => {
		let resultVolume = currentVolumeLevel + 0.05;

		if (resultVolume <= 1) {
			(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(resultVolume);
		} else {
			(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(1);
		}
	};

	const handleVolumeSlider = (value: number | number[]) => {
		const newValue = typeof value === 'number' ? value : (value ?? [])[0];
		(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(newValue);
	};

	const handleButtonHeart = () => {
		heartAnimation('bottomControlbarHearts');
		var tempObj = {
			sender: {
				userId: userId !== '' ? userId : 'notLoggedId',
				name: username !== '' ? username : 'notLoggedName',
				email: email !== '' ? email : 'notLoggedEmail',
				admin: false,
			},
			message: {
				emoji: {
					unicode: '\u2764\uFE0F',
					level: 1,
				},
			},
		};

		window.parent.postMessage(
			{
				origin: 'Qbrick.Interact.Widget.Element',
				identifier: 'qbrick-player-analytics-message',
				command: {
					type: 'AnalyticsEvent',
					action: 'click',
					data: 'reaction',
					mediaName: mediaName,
					mediaId: currentMediaId,
					accountId: accountId,
					duration: duration,
					isMediaLive: isMediaLive,
					currentPosition: isMediaLive ? Date.now() : currentPosition,
				},
			},
			'*'
		);

		fetch(reactionUrl, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(tempObj),
		});
	};

	const handleButtonShoppingCart = () => {
		dispatch(setShowChatWindow(false));
		dispatch(setShowTranscriptionsModule(false));
		setTimeout(() => {
			dispatch(setShowShoppingCart(!showShoppingCart));
		}, 50);
	};

	const handleButtonChatToggle = () => {
		dispatch(setShowShoppingCart(false));
		dispatch(setShowTranscriptionsModule(false));
		setTimeout(() => {
			dispatch(setShowChatWindow(!showChatWindow));
		}, 50);
	};

	const renderCastBtn = useCallback(
		({ tabIndexValue, hoverStyle }) => {
			if (!props.settings.bottomControls.showCastDevices) {
				return;
			}

			if (isSafari || isIOS) {
				return (
					<ControlButton
						tabIndex={tabIndexValue}
						hoverStyle={hoverStyle}
						ariaLabel={t('AIR_PLAY')}
						tooltip={t('AIR_PLAY')}
						icon={<QPlayerSVGElement.AirPlay />}
						onClick={handleAirPlayBtnClick}
					/>
				);
			}

			//* See https://stackoverflow.com/questions/57660234/how-can-i-check-if-a-browser-is-chromium-based */
			const isChromium = !!(window as any).chrome;
			return (
				<>
					{isChromium && (
						<ControlButton
							tabIndex={tabIndexValue}
							hoverStyle={hoverStyle}
							ariaLabel={t('CHROME_CAST')}
							onClick={handleChromecastBtnClick}
							tooltip={t('CHROME_CAST')}
							icon={<QPlayerSVGElement.Chromecast />}
						/>
					)}
				</>
			);
		},
		[handleChromecastBtnClick, handleAirPlayBtnClick, props.settings.bottomControls, t]
	);

	const renderPiPBtn = useCallback(
		({ tabIndexValue, hoverStyle }) => {
			const showPiPSetting = props.settings.bottomControls.showPictureInPicture && !props.queryParams?.scenarioId;
			if (isFirefox || !showPiPSetting) {
				return;
			}

			return (
				<ControlButton
					tabIndex={tabIndexValue}
					tooltip={document.pictureInPictureElement ? t('EXIT_PICTURE_IN_PICTURE') : t('ENTER_PICTURE_IN_PICTURE')}
					ariaLabel={document.pictureInPictureElement ? t('EXIT_PICTURE_IN_PICTURE') : t('ENTER_PICTURE_IN_PICTURE')}
					onClick={handlePictureInPictureBtnClick}
					icon={document.pictureInPictureElement ? <QPlayerSVGElement.ExitPiP /> : <QPlayerSVGElement.EnterPiP />}
					hoverStyle={hoverStyle}
				/>
			);
		},
		[handlePictureInPictureBtnClick, props.queryParams?.scenarioId, props.settings.bottomControls.showPictureInPicture, t]
	);

	useEffect(() => {
		window.addEventListener('fullscreenchange', handleFullscreenchange);

		return () => {
			window.removeEventListener('fullscreenchange', handleFullscreenchange);
		};
	}, [handleFullscreenchange, props.settings.playbackOptions.muteAtStart, dispatch]);

	useEffect(() => {
		if (!reactionId) {
			return;
		}
		heartAnimation('bottomControlbarHearts');
	}, [reactionId]);

	const isShowPlaybackSpeedSettings = props.settings.bottomControls?.showPlaybackSpeedSettings && !isMediaLive && mediaType !== 'scenario';

	if (props.isSmallScreen) {
		return (
			<div className={`bottomControlbarBottomRow ${props.isShowTimeline ? '' : 'timeline-hidden'}`}>
				<div
					className="bottomControlbarLeft"
					style={{
						visibility: props.settings.bottomControls.showPlaybackControls ? 'visible' : 'hidden',
					}}
				>
					{props.settings.bottomControls.showPlayPauseButton && !showCenterControls && (
						<ControlButton
							tabIndex={tabIndexValue}
							ariaLabel={isPlaying ? t('PAUSE') : t('PLAY')}
							className="bottomControlbarButtonPlay"
							hoverStyle={hoverStyle}
							onClick={handleButtonPlayPauseClick}
							tooltip={isPlaying ? t('PAUSE') : t('PLAY')}
							icon={isPlaying ? <QPlayerSVGElement.Pause /> : <QPlayerSVGElement.Play />}
						/>
					)}

					{props.settings.bottomControls.showAudioControls && isUsedOnMobile(props.queryParams) && (
						<ControlButton
							tabIndex={tabIndexValue}
							role={props.isSmallScreen ? 'button' : 'slider'}
							aria-valuenow={getVolumePercentage(currentVolumeLevel)}
							aria-valuemin="0"
							aria-valuemax="100"
							aria-valuetext={`${t('VOLUME')} ${getVolumePercentage(currentVolumeLevel)}%`}
							aria-label={`${currentVolumeLevel !== 0 ? t('MUTE_BUTTON') : t('UNMUTE_BUTTON')}, ${t(props.isSmallScreen ? 'VOLUME_SLIDER_MOBILE' : 'VOLUME_SLIDER')}`}
							className="bottomControlbarVolume"
							tooltip={currentVolumeLevel !== 0 ? t('MUTE_BUTTON') : t('UNMUTE_BUTTON')}
							onClick={currentVolumeLevel !== 0 ? handleButtonMute : handleButtonUnmute}
						>
							{currentVolumeLevel !== 0 ? (
								<div className="bottomControlbarVolumeMute">
									{currentVolumeLevel < 0.5 ? <QPlayerSVGElement.VolumeSmall /> : <QPlayerSVGElement.Mute />}
								</div>
							) : (
								<div className="bottomControlbarVolumeUnmute">
									<QPlayerSVGElement.Unmute />
								</div>
							)}
						</ControlButton>
					)}

					{props.settings.bottomControls.showTime && !isMediaLive && (
						<div
							className="bottomControlbarTime"
							aria-label={
								t('POSITION_TEXT') +
								NumberHelper.formatHumanize(currentPosition, t) +
								t('LABEL_OF') +
								NumberHelper.formatHumanize(duration, t)
							}
						>
							{times.split('').map((t, i) => (
								<span key={i} aria-hidden="true" className={t === ':' ? 'separator' : t === '/' ? 'splash' : ''}>
									{t}
								</span>
							))}
						</div>
					)}

					{isMediaLive && (
						<div className="bottomControlbarLive">
							<div
								role="button"
								aria-label={t(isAtLive ? 'LIVE_MODE' : 'DVR_BUTTON')}
								className="bottomControlbarLiveIcon"
								onClick={goToLive}
							>
								<QPlayerSVGElement.Live atLive={isAtLive} />
								<div className="controlbar__control-tooltip">
									<div className="controlbar__control-tooltip-text">{t('TIMESHIFT')}</div>
								</div>
							</div>
						</div>
					)}

					{props.settings.bottomControls.showAudioControls && !isUsedOnMobile(props.queryParams) && (
						<ControlButton
							tabIndex={tabIndexValue}
							role="slider"
							aria-valuenow={getVolumePercentage(currentVolumeLevel)}
							aria-valuemin="0"
							aria-valuemax="100"
							aria-valuetext={`${t('VOLUME')} ${getVolumePercentage(currentVolumeLevel)}%`}
							aria-label={`${currentVolumeLevel !== 0 ? t('MUTE_BUTTON') : t('UNMUTE_BUTTON')}, ${t(props.isSmallScreen ? 'VOLUME_SLIDER_MOBILE' : 'VOLUME_SLIDER')}`}
							hoverStyle={hoverStyle}
							className="bottomControlbarVolume"
							onMouseEnter={() => {
								setVolumeHovered(true);
							}}
							onMouseLeave={() => {
								setVolumeHovered(false);
							}}
							onFocus={() => setVolumeHovered(true)}
							onBlur={() => setVolumeHovered(false)}
							onKeyDown={handleKeydownVolumeButton}
							onClick={currentVolumeLevel !== 0 ? handleButtonMute : handleButtonUnmute}
						>
							{currentVolumeLevel !== 0 ? (
								<div className="bottomControlbarVolumeMute" onClick={handleButtonMute}>
									{currentVolumeLevel < 0.5 ? <QPlayerSVGElement.VolumeSmall /> : <QPlayerSVGElement.Mute />}
								</div>
							) : (
								<div className="bottomControlbarVolumeUnmute" onClick={handleButtonUnmute}>
									<QPlayerSVGElement.Unmute />
								</div>
							)}

							<div
								className={joinClassNames(['bottomControlsVolumeSlider', volumeHovered ? 'bottomControlsVolumeSlider--visible' : ''])}
							>
								<div className="bottomControlsVolumeSliderWrapper">
									<div className="bottomControlsVolume--Slider">
										<VolumeSlider
											tabIndex={-1}
											onMouseDown={(e) => e.stopPropagation()}
											data-control-type="slider-control" // Every slider should have this attribute to be able to work.
											value={currentVolumeLevel}
											onChange={(_e, newValue) => handleVolumeSlider(newValue)}
											min={0}
											max={1}
											step={0.05}
											aria-valuetext={`${t('VOLUME')} ${getVolumePercentage(currentVolumeLevel)}%`}
											aria-label={`${currentVolumeLevel !== 0 ? t('MUTE_BUTTON') : t('UNMUTE_BUTTON')}, ${t('VOLUME_SLIDER')}`}
											className="bottomControlsVolumeSliderSpan"
											ThumbComponent={(props) => <span {...props} tabIndex={-1} />}
										/>
										<div
											className={joinClassNames([
												'controlbar__control-tooltip',
												'controlbar__control-tooltip--light',
												volumeHovered ? 'controlbar__control-tooltip--visible' : '',
											])}
										>
											<div className="controlbar__control-tooltip-text">{getVolumePercentage(currentVolumeLevel) + '%'}</div>
										</div>
									</div>
									<div className="bottomControlsReduceVolume" onClick={handleButtonReduceVolume}>
										<QPlayerSVGElement.VolumeDecrease />
									</div>
									<div className="bottomControlsIncreaseVolume" onClick={handleButtonIncreaseVolume}>
										<QPlayerSVGElement.VolumeIncrease />
									</div>
								</div>
							</div>
						</ControlButton>
					)}
				</div>
				<div className="bottomControlbarRight" ref={(r) => (bottomControlbarRight.current = r)}>
					{props.settings.bottomControls.showLanguageSettingsButton && (hasAudio || hasSubtitle || showTranscriptSettings) && (
						<ControlButton
							tabIndex={tabIndexValue}
							ariaLabel={t('AUDIO_SUBTITLES_BUTTON')}
							className="bottomControlbarLanguageMenu"
							hoverStyle={hoverStyle}
							onClick={handleButtonLanguageMenu}
							tooltip={t('AUDIO_SUBTITLES')}
							icon={<QPlayerSVGElement.LanguageSetting />}
							aria-expanded={openSettingMenu?.type === 'language' ? 'true' : 'false'}
							onMouseDown={(e) => {
								if (openSettingMenu?.type === 'language') {
									e.stopPropagation();
								}
							}}
						/>
					)}
					{props.settings.bottomControls.showSettingsButton &&
						(props.settings.bottomControls.showVideoQualitySettings || isShowPlaybackSpeedSettings) && (
							<ControlButton
								tabIndex={tabIndexValue}
								ariaLabel={t('SETTINGS_BUTTON')}
								className="bottomControlbarSettings"
								onClick={handleButtonSettings}
								tooltip={t('SETTINGS')}
								hoverStyle={hoverStyle}
								icon={<QPlayerSVGElement.Settings />}
								aria-expanded={openSettingMenu?.type === 'quality' ? 'true' : 'false'}
								onMouseDown={(e) => {
									if (openSettingMenu?.type === 'quality') {
										e.stopPropagation();
									}
								}}
							/>
						)}
					{renderCastBtn({ tabIndexValue, hoverStyle })}
					{renderPiPBtn({ tabIndexValue, hoverStyle })}
					{props.settings.bottomControls.showFullscreen && (
						<>
							{!fullscreenMode ? (
								<ControlButton
									tabIndex={tabIndexValue}
									className="bottomControlbarFullscreenEnable"
									ariaLabel={t('FULLSCREEN')}
									hoverStyle={hoverStyle}
									onClick={handleButtonFullscreenEnter}
									icon={<QPlayerSVGElement.FullScreen />}
									tooltip={t('FULLSCREEN')}
								/>
							) : (
								<ControlButton
									tabIndex={tabIndexValue}
									ariaLabel={t('FULLSCREEN_EXIT')}
									hoverStyle={hoverStyle}
									className="bottomControlbarFullscreenExit"
									onClick={handleButtonFullscreenExit}
									icon={<QPlayerSVGElement.ExitFullScreen />}
									tooltip={t('FULLSCREEN_EXIT')}
								/>
							)}
						</>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={`bottomControlbarBottomRow ${props.isShowTimeline ? '' : 'timeline-hidden'}`}>
			<div
				className="bottomControlbarLeft"
				style={{
					visibility: props.settings.bottomControls.showPlaybackControls ? 'visible' : 'hidden',
				}}
			>
				{props.settings.bottomControls.showPlayPauseButton && !showCenterControls && (
					<ControlButton
						tabIndex={tabIndexValue}
						ariaLabel={isPlaying ? t('PAUSE') : t('PLAY')}
						className="bottomControlbarButtonPlay"
						hoverStyle={hoverStyle}
						onClick={handleButtonPlayPauseClick}
						tooltip={isPlaying ? t('PAUSE') : t('PLAY')}
						icon={isPlaying ? <QPlayerSVGElement.Pause /> : <QPlayerSVGElement.Play />}
					/>
				)}

				{props.settings.bottomControls.showTime && !isMediaLive && (
					<div
						className="bottomControlbarTime"
						aria-label={
							t('POSITION_TEXT') +
							NumberHelper.formatHumanize(currentPosition, t) +
							t('LABEL_OF') +
							NumberHelper.formatHumanize(duration, t)
						}
					>
						{times.split('').map((t, i) => (
							<span key={i} aria-hidden="true" className={t === ':' ? 'separator' : t === '/' ? 'splash' : ''}>
								{t}
							</span>
						))}
					</div>
				)}

				{isMediaLive && (
					<div className="bottomControlbarLive">
						<div
							tabIndex={tabIndexValue}
							role="button"
							aria-label={t(isAtLive ? 'LIVE_MODE' : 'DVR_BUTTON')}
							className="bottomControlbarLiveIcon"
							onClick={goToLive}
							onKeyDown={(e) => handleKeyDownOnButton(e, goToLive)}
						>
							<QPlayerSVGElement.Live atLive={isAtLive} />
							<div className="controlbar__control-tooltip">
								<div className="controlbar__control-tooltip-text">{t('TIMESHIFT')}</div>
							</div>
						</div>
					</div>
				)}

				{!showCenterControls &&
					props.settings.bottomControls.showRewindForwardButtons &&
					(LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_DVR ||
						LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_ON_DEMAND) && (
						<>
							<ControlButton
								tabIndex={tabIndexValue}
								ariaLabel={t('REWIND')}
								className="bottomControlbarButtonRewind"
								hoverStyle={hoverStyle}
								onClick={handleButtonRewind}
								tooltip={t('REWIND')}
								icon={<QPlayerSVGElement.Rewind />}
							/>
							<ControlButton
								tabIndex={tabIndexValue}
								ariaLabel={t('FORWARD')}
								className="bottomControlbarButtonForward"
								hoverStyle={hoverStyle}
								onClick={isAtLive ? undefined : handleButtonForward}
								tooltip={t('FORWARD')}
								icon={<QPlayerSVGElement.Forward />}
							/>
						</>
					)}
				{props.settings.bottomControls.showAudioControls && (
					<ControlButton
						tabIndex={tabIndexValue}
						role="slider"
						aria-valuenow={getVolumePercentage(currentVolumeLevel)}
						aria-valuemin="0"
						aria-valuemax="100"
						aria-valuetext={`${t('VOLUME')} ${getVolumePercentage(currentVolumeLevel)}%`}
						aria-label={`${currentVolumeLevel !== 0 ? t('MUTE_BUTTON') : t('UNMUTE_BUTTON')}, ${t(props.isSmallScreen ? 'VOLUME_SLIDER_MOBILE' : 'VOLUME_SLIDER')}`}
						hoverStyle={hoverStyle}
						className="bottomControlbarVolume"
						onMouseEnter={() => {
							setVolumeHovered(true);
						}}
						onMouseLeave={() => {
							setVolumeHovered(false);
						}}
						onFocus={!props.isSmallScreen ? () => setVolumeHovered(true) : undefined}
						onBlur={!props.isSmallScreen ? () => setVolumeHovered(false) : undefined}
						onKeyDown={handleKeydownVolumeButton}
						onClick={props.isSmallScreen ? (currentVolumeLevel !== 0 ? handleButtonMute : handleButtonUnmute) : undefined}
					>
						{currentVolumeLevel !== 0 ? (
							<div className="bottomControlbarVolumeMute" onClick={props.isSmallScreen ? undefined : handleButtonMute}>
								{currentVolumeLevel < 0.5 ? <QPlayerSVGElement.VolumeSmall /> : <QPlayerSVGElement.Mute />}
							</div>
						) : (
							<div className="bottomControlbarVolumeUnmute" onClick={props.isSmallScreen ? undefined : handleButtonUnmute}>
								<QPlayerSVGElement.Unmute />
							</div>
						)}
						{!props.isSmallScreen && (
							<div
								className={joinClassNames(['bottomControlsVolumeSlider', volumeHovered ? 'bottomControlsVolumeSlider--visible' : ''])}
							>
								<div className="bottomControlsVolumeSliderWrapper">
									<div className="bottomControlsVolume--Slider">
										<VolumeSlider
											tabIndex={-1}
											onMouseDown={(e) => e.stopPropagation()}
											data-control-type="slider-control" // Every slider should have this attribute to be able to work.
											value={currentVolumeLevel}
											onChange={(_e, newValue) => handleVolumeSlider(newValue)}
											min={0}
											max={1}
											step={0.05}
											aria-valuetext={`${t('VOLUME')} ${getVolumePercentage(currentVolumeLevel)}%`}
											aria-label={`${currentVolumeLevel !== 0 ? t('MUTE_BUTTON') : t('UNMUTE_BUTTON')}, ${t(props.isSmallScreen ? 'VOLUME_SLIDER_MOBILE' : 'VOLUME_SLIDER')}`}
											className='bottomControlsVolumeSliderSpan'
											ThumbComponent={(props) => <span {...props} tabIndex={-1} />}
										/>
										<div
											className={joinClassNames([
												'controlbar__control-tooltip',
												'controlbar__control-tooltip--light',
												volumeHovered ? 'controlbar__control-tooltip--visible' : '',
											])}
										>
											<div className="controlbar__control-tooltip-text">{getVolumePercentage(currentVolumeLevel) + '%'}</div>
										</div>
									</div>
									<div className="bottomControlsReduceVolume" onClick={handleButtonReduceVolume}>
										<QPlayerSVGElement.VolumeDecrease />
									</div>
									<div className="bottomControlsIncreaseVolume" onClick={handleButtonIncreaseVolume}>
										<QPlayerSVGElement.VolumeIncrease />
									</div>
								</div>
							</div>
						)}
					</ControlButton>
				)}
			</div>
			<div className="bottomControlbarRight" ref={(r) => (bottomControlbarRight.current = r)}>
				{props.settings.engagementFeatures.enabled && props.settings.engagementFeatures.showLiveLikes && !props.isSmallScreen && (
					<ControlButton
						tabIndex={tabIndexValue}
						className="bottomControlbarHeart"
						ariaLabel={t('SEND_HEARTS')}
						hoverStyle={hoverStyle}
						onClick={handleButtonHeart}
						tooltip={t('SEND_HEARTS')}
						icon={<QPlayerSVGElement.Heart />}
					>
						{(!props.isSmallScreen || props.playerHover) && <div className="bottomControlbarHearts" />}
					</ControlButton>
				)}
				{viewerEngagementModuleActivated &&
					props.settings.engagementFeatures.enabled &&
					props.settings.engagementFeatures.showChat &&
					!props.isSmallScreen && (
						<ControlButton
							tabIndex={tabIndexValue}
							className="bottomControlbarChatTogle"
							ariaLabel={t(showChatWindow ? 'CHAT_WINDOW_CLOSE' : 'CHAT_WINDOW_OPEN')}
							hoverStyle={hoverStyle}
							onClick={handleButtonChatToggle}
							tooltip={showChatWindow ? t('CHAT_WINDOW_CLOSE') : t('CHAT_WINDOW_OPEN')}
							icon={showChatWindow ? <QPlayerSVGElement.CloseChat /> : <QPlayerSVGElement.ShowChat />}
						/>
					)}
				{props.settings.engagementFeatures.enabled && props.settings.engagementFeatures.showShoppingCart && !props.isSmallScreen && (
					<ControlButton
						tabIndex={tabIndexValue}
						className="bottomControlbarShopping"
						ariaLabel={t(showShoppingCart ? 'SHOPPING_CART_CLOSE' : 'SHOPPING_CART_OPEN')}
						hoverStyle={hoverStyle}
						onClick={handleButtonShoppingCart}
						tooltip={showShoppingCart ? t('SHOPPING_CART_CLOSE') : t('SHOPPING_CART_OPEN')}
						icon={<QPlayerSVGElement.ShoppingCart />}
					>
						<div className="bottomControlbarShoppingQuantityCounter" style={primaryColor}>
							{cartQuantity}
						</div>
					</ControlButton>
				)}
				{props.settings.bottomControls.showLanguageSettingsButton && (hasAudio || hasSubtitle || showTranscriptSettings) && (
					<ControlButton
						tabIndex={tabIndexValue}
						ariaLabel={t('AUDIO_SUBTITLES_BUTTON')}
						className="bottomControlbarLanguageMenu"
						hoverStyle={hoverStyle}
						onClick={handleButtonLanguageMenu}
						tooltip={t('AUDIO_SUBTITLES')}
						icon={<QPlayerSVGElement.LanguageSetting />}
						style={openSettingMenu?.type === 'language' || openSettingMenu?.type === 'subtitleSettings' ? hoverStyle : {}}
						aria-expanded={openSettingMenu?.type === 'language' ? 'true' : 'false'}
						onMouseDown={(e) => {
							if (openSettingMenu?.type === 'language') {
								e.stopPropagation();
							}
						}}
					/>
				)}
				{props.settings.bottomControls.showSettingsButton &&
					(props.settings.bottomControls.showVideoQualitySettings || isShowPlaybackSpeedSettings) && (
						<ControlButton
							tabIndex={tabIndexValue}
							ariaLabel={t('SETTINGS_BUTTON')}
							className="bottomControlbarSettings"
							hoverStyle={hoverStyle}
							onClick={handleButtonSettings}
							tooltip={t('SETTINGS')}
							icon={<QPlayerSVGElement.Settings />}
							style={openSettingMenu?.type === 'quality' ? hoverStyle : {}}
							aria-expanded={openSettingMenu?.type === 'quality' ? 'true' : 'false'}
							onMouseDown={(e) => {
								if (openSettingMenu?.type === 'quality') {
									e.stopPropagation();
								}
							}}
						/>
					)}
				{renderCastBtn({ tabIndexValue, hoverStyle })}
				{renderPiPBtn({ tabIndexValue, hoverStyle })}
				{props.settings.bottomControls.showFullscreen && (
					<>
						{!fullscreenMode ? (
							<ControlButton
								className="bottomControlbarFullscreenEnable"
								tabIndex={tabIndexValue}
								ariaLabel={t('FULLSCREEN')}
								hoverStyle={hoverStyle}
								onClick={handleButtonFullscreenEnter}
								tooltip={t('FULLSCREEN')}
								icon={<QPlayerSVGElement.FullScreen />}
							/>
						) : (
							<ControlButton
								tabIndex={tabIndexValue}
								ariaLabel={t('FULLSCREEN_EXIT')}
								className="bottomControlbarFullscreenExit"
								hoverStyle={hoverStyle}
								onClick={handleButtonFullscreenExit}
								tooltip={t('FULLSCREEN_EXIT')}
								icon={<QPlayerSVGElement.ExitFullScreen />}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default BottomControlSetting;
